import { useEffect, useState } from 'react';
import { usePortalSetting } from '@/hooks/usePortalSetting';

export default function HeroCarousel() {
  // 官网配置接口，失败时降级为 mock 数据
  const { heroSlides } = usePortalSetting();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = heroSlides.length;

  useEffect(() => {
    if (paused || total === 0) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused, total]);

  if (total === 0) return null;

  const slide = heroSlides[current % total];

  const goTo = (index: number) => setCurrent((index + total) % total);

  return (
    <section
      id="home"
      className="relative w-full h-[92vh] min-h-[600px] overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-out ${
            index === current ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 中西医结合智慧生态平台`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/60 to-foreground-950/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-center">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-background-50/30 bg-background-50/10 backdrop-blur text-background-50 text-xs md:text-sm mb-5">
              <i className="ri-award-line text-secondary-300"></i>
              {slide.tag}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight text-balance mb-5">
              {slide.title}
            </h1>
            <p className="text-sm md:text-base text-background-100/85 leading-relaxed mb-8 max-w-2xl">
              {slide.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <a
                href={slide.primaryHref}
                className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium px-7 py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                {slide.primaryText}
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href={slide.secondaryHref}
                className="inline-flex items-center justify-center gap-2 border border-background-50/40 text-background-50 hover:bg-background-50/10 font-medium px-7 py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                {slide.secondaryText}
              </a>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一屏"
        onClick={() => goTo(current - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一屏"
        onClick={() => goTo(current + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-8 left-0 w-full z-20 flex items-center justify-center gap-3">
        {heroSlides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`第${index + 1}屏`}
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === current ? 'w-8 bg-primary-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}